import { calculateFontSize, calculateTextWidth, getSpecialFontFamily, getSpecialFontSize, getSpecialFontStyle, getSpecialFontForLanguage } from './presetLayout';
import { DEFAULT_CANVAS_CONFIG as defaults } from '../constants/canvasDefaults';

// A versioned empty layout is intentional. Only the migration may initialize
// legacy layouts. Presets supply style; each box owns geometry and languages.
export const PRIVATE_LAYOUT_VERSION = 1;
export const SHARED_PAGE_LANGUAGE = '__page_shared__';
export const STYLE_KEYS = [
  'fontFamily', 'fontFamilySecondary', 'fontStyle', 'fontStyleSecondary',
  'fontSize', 'fontSizeMode', 'fontSizeRules', 'minFontSize', 'lineHeight',
  'alignment', 'padding', 'backgroundMode', 'whiteBackground', 'showDivider',
  'separatorHeight', 'separatorPadding', 'separatorSpacing', 'fill',
];
export const LAYOUT_KEYS = ['textWidth', 'textWidthMode', 'textWidthPercentage', 'maxWidth', 'textVisibility', 'verticalAnchor', 'verticalOffset'];
export const pick = (config = {}, keys = STYLE_KEYS) => Object.fromEntries(
  keys.filter((key) => config?.[key] !== undefined).map((key) => [key, config[key]]),
);
export const BUILTIN_TEXT_STYLE = Object.freeze({
  fontFamily: defaults.fontFamily, fontFamilySecondary: defaults.fontFamily,
  fontStyle: 'normal', fontStyleSecondary: 'italic', fontSize: defaults.fontSizeBig,
  fontSizeMode: 'fixed', lineHeight: defaults.textSeparation,
  alignment: defaults.alignment, padding: defaults.padding, backgroundMode: 'text',
  showDivider: false, separatorHeight: defaults.separatorHeight,
  separatorPadding: defaults.separatorPadding, separatorSpacing: 0,
});
export const BUILTIN_PRESET = Object.freeze({ name: 'Built-in text style', config: BUILTIN_TEXT_STYLE });

export function resolveBoxPreset(box, presets = [], bookDefaultId) {
  const assignedId = box?.stylePresetId !== undefined ? box.stylePresetId : box?.style?.presetId || box?.presetId;
  const assigned = presets.find((preset) => preset._id === assignedId);
  const bookDefault = presets.find((preset) => preset._id === bookDefaultId);
  const source = assigned || bookDefault || BUILTIN_PRESET;
  // Legacy snapshots preserve the existing visual result until explicit
  // migration separates them. They must never become inherited overrides.
  const legacy = box?.schemaVersion !== PRIVATE_LAYOUT_VERSION;
  const legacyConfig = legacy ? box?.style?.layoutConfig || box?.style?.presetConfig || box?.presetConfig || {} : {};
  const overrides = legacy ? pick(box?.style || {}) : pick(box?.styleOverrides || {});
  const config = { ...BUILTIN_TEXT_STYLE, ...pick(bookDefault?.config), ...pick(source.config), ...pick(legacyConfig), ...overrides,
    ...pick(box?.layoutConfig || (legacy ? legacyConfig : {}), LAYOUT_KEYS),
    textVisibility: box?.layoutConfig?.textVisibility || box?.style?.textVisibility || (box?.mode === 'single' ? 'primary' : 'both'),
  };
  config.padding = typeof config.padding === 'number'
    ? { left: config.padding, right: config.padding, top: config.padding, bottom: config.padding }
    : { ...defaults.padding, ...config.padding };
  return { ...source, config };
}

// Adapt to the existing Konva renderer while keeping cached measurements out of
// the persisted style hierarchy. All callers render the same effective style.
export const getBoxSlotStyle = (style, language, index) => {
  const special = getSpecialFontForLanguage(language);
  return {
    fontSize: getSpecialFontSize(special, style.fontSize),
    fontFamily: getSpecialFontFamily(special, index ? style.fontFamilySecondary || style.fontFamily : style.fontFamily),
    fontStyle: getSpecialFontStyle(special, index ? style.fontStyleSecondary || 'italic' : style.fontStyle || 'normal'),
  };
};

export function resolveRenderableBox(box, presets = [], bookDefaultId, canvasSize, languages) {
  const preset = resolveBoxPreset(box, presets, bookDefaultId);
  if (box?.schemaVersion !== PRIVATE_LAYOUT_VERSION) return { box, preset };
  const config = preset.config;
  const activeLanguages = languages?.length ? languages : box.slotLanguages || ['en'];
  const visibleLanguages = config.textVisibility === 'primary' ? activeLanguages.slice(0, 1)
    : config.textVisibility === 'secondary' ? activeLanguages.slice(1, 2) : activeLanguages;
  const totalChars = visibleLanguages.reduce((count, language) => count + String(box.texts?.[language] || '').trim().length, 0);
  const fontSize = calculateFontSize(preset, totalChars);
  const padding = typeof config.padding === 'number' ? config.padding : config.padding?.left ?? 24;
  const gap = Number(config.separatorPadding || 0) * (config.separatorSpacing === true ? 1 : Number(config.separatorSpacing || 0));
  const style = { ...box.style, ...pick(config), fontSize, padding, separatorPadding: gap,
    presetFlow: Boolean(box.style?.presetFlow),
    positionMode: box.layoutConfig?.positionMode || box.style?.positionMode || 'custom',
    textVisibility: config.textVisibility };
  let position = box.position;
  const automatic = box.layoutConfig?.positionMode === 'automatic';
  const width = automatic && canvasSize ? calculateTextWidth(preset, canvasSize) : Math.max(20, (position?.width || 260) - padding * 2);
  const heights = window.Konva?.Text ? visibleLanguages.map((language) => {
    const slot = getBoxSlotStyle(style, language, activeLanguages.indexOf(language));
    const node = new window.Konva.Text({ text: String(box.texts?.[language] || ' '), width, ...slot,
      lineHeight: config.lineHeight, align: config.alignment });
    const height = node.height(); node.destroy(); return height;
  }) : [];
  const divider = config.showDivider ? Number(config.separatorHeight || 0) : 0;
  const textHeight = heights.reduce((sum, value) => sum + value, 0) + Math.max(0, heights.length - 1) * (2 * gap + divider);
  // Recalculate only present boxes with an explicit automatic placement rule.
  if (automatic && canvasSize && heights.length) {
    const centerY = config.verticalAnchor === 'divider' && heights.length === 2
      ? canvasSize.height / 2 - heights[0] - gap - divider / 2 : (canvasSize.height - textHeight) / 2;
    position = { x: (canvasSize.width - width) / 2 - padding, y: centerY + Number(config.verticalOffset || 0) - padding,
      width: width + padding * 2, height: textHeight + padding * 2 };
  }
  return { preset, box: { ...box, position, style },
    textOverflows: Boolean(position && textHeight > position.height - padding * 2 + 1) };
}

export function orderLayoutBoxes(items, entries = []) {
  const order = new Map(entries.map((entry, index) => [entry.id || entry.entry_id || entry.key, index]));
  const rank = (item) => item.role === 'main' || item.sourceType === 'page_main' ? -1
    : order.get(item.sourceTextEntryId || item.slotKey) ?? entries.length;
  return [...items].sort((first, second) => rank(first) - rank(second));
}

export const compatibleFormat = (format, canvas) => Boolean(format && canvas &&
  Number(format.width) > 0 && Number(format.height) > 0 &&
  Number(format.width) === Number(canvas.width) && Number(format.height) === Number(canvas.height));

export function versionBox(item) {
  if (item.schemaVersion === PRIVATE_LAYOUT_VERSION) return item;
  const style = item.style || {};
  const snapshot = style.presetConfig || item.presetConfig || {};
  const overrides = style.useCustomLayout ? pick(style.layoutConfig || {}) : {};
  Object.entries(pick(style)).forEach(([key, value]) => {
    let original = snapshot[key];
    if (key === 'padding' && typeof original === 'object') original = original?.left;
    if (key === 'separatorPadding') original = Number(original || 0) * Number(snapshot.separatorSpacing || 0);
    if (JSON.stringify(value) !== JSON.stringify(original)) {
      overrides[key] = value;
      if (key === 'separatorPadding') overrides.separatorSpacing = 1;
    }
  });
  if (style.fontSize !== undefined) Object.assign(overrides, { fontSize: style.fontSize, fontSizeMode: 'fixed' });
  return { ...item, schemaVersion: PRIVATE_LAYOUT_VERSION,
    stylePresetId: style.presetId || item.presetId || null,
    styleOverrides: overrides,
    layoutConfig: { ...pick(snapshot, LAYOUT_KEYS),
      positionMode: style.positionMode === 'custom' ? 'custom' : 'fixed',
      textVisibility: style.textVisibility || snapshot.textVisibility || 'both' },
  };
}

// Explicit insertion only: neither loading nor rendering creates a missing box.
export function createLayoutBox({ sourceId, name, texts = {}, languages = [], canvasSize,
  textVisibility = 'both', presets = [], bookDefaultId }) {
  const main = sourceId === 'page-main';
  const width = Math.max(20, Math.min(defaults.widthShort, canvasSize.width - 2 * defaults.padding.left));
  const box = {
    id: `text-item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    schemaVersion: PRIVATE_LAYOUT_VERSION, name: main ? 'Main text' : name || 'Text box',
    role: main ? 'main' : 'additional', sourceType: main ? 'page_main' : 'page_text_entry',
    sourceTextEntryId: sourceId, pageLanguage: SHARED_PAGE_LANGUAGE,
    slotLanguages: languages, mode: languages.length === 1 ? 'single' : 'bilingual',
    stylePresetId: null, styleOverrides: {}, style: { presetFlow: true },
    layoutConfig: { positionMode: 'automatic', textVisibility, textWidthMode: 'fixed', textWidth: width },
    position: { x: (canvasSize.width - width) / 2, y: canvasSize.height / 2 - 50, width, height: 100 },
    texts, visibleOnCanvas: true,
  };
  return { ...box, position: resolveRenderableBox(box, presets, bookDefaultId, canvasSize, languages).box.position };
}
